import React, { createContext, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { onValue, ref, update as updateFirebase } from "firebase/database";
import { selectLogInUsername } from "../common/authenticateDashboard/authSlice";
import { db } from "../firebase";

const DEFAULT_VISIBILITY = Object.freeze({
  adsAgentEnabled: false,
  seoAgentEnabled: false,
  landingAbEnabled: false,
});

const AgentVisibilityContext = createContext({
  ...DEFAULT_VISIBILITY,
  loading: false,
  saving: false,
  updateVisibility: async () => DEFAULT_VISIBILITY,
});

function normalizeVisibility(value = {}) {
  return {
    adsAgentEnabled: value.adsAgentEnabled === true,
    seoAgentEnabled: value.seoAgentEnabled === true,
    landingAbEnabled: value.landingAbEnabled === true,
  };
}

function storageKey(collectionName) {
  return `cf-agent-visibility:v2:${collectionName}`;
}

function readCachedVisibility(collectionName) {
  if (!collectionName) return DEFAULT_VISIBILITY;
  try {
    const cached = window.localStorage.getItem(storageKey(collectionName));
    return cached ? normalizeVisibility(JSON.parse(cached)) : DEFAULT_VISIBILITY;
  } catch {
    return DEFAULT_VISIBILITY;
  }
}

function cacheVisibility(collectionName, visibility) {
  if (!collectionName) return;
  try {
    window.localStorage.setItem(storageKey(collectionName), JSON.stringify(visibility));
  } catch {
    // Storage can be unavailable in private browsing; the live state still works.
  }
}

function AgentVisibilityStateProvider({ collectionName, children }) {
  const [visibility, setVisibility] = useState(() => readCachedVisibility(collectionName));
  const [loading, setLoading] = useState(Boolean(collectionName));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!collectionName) {
      setVisibility(DEFAULT_VISIBILITY);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const statusRef = ref(db, `${collectionName}/graph-status`);
    const unsubscribe = onValue(
      statusRef,
      (snapshot) => {
        const status = snapshot.val() || {};
        const next = normalizeVisibility({
          adsAgentEnabled: status.ads_agent_enabled,
          seoAgentEnabled: status.seo_agent_enabled,
          landingAbEnabled: status.landing_ab_enabled,
        });
        setVisibility(next);
        cacheVisibility(collectionName, next);
        setLoading(false);
      },
      () => {
        setVisibility(DEFAULT_VISIBILITY);
        setLoading(false);
      },
    );

    return unsubscribe;
  }, [collectionName]);

  const updateVisibility = async (changes, adminEmail) => {
    if (!collectionName) throw new Error("No client is selected.");
    const next = normalizeVisibility({ ...visibility, ...changes });
    setSaving(true);

    try {
      await updateFirebase(ref(db, `${collectionName}/graph-status`), {
        ads_agent_enabled: next.adsAgentEnabled,
        seo_agent_enabled: next.seoAgentEnabled,
        landing_ab_enabled: next.landingAbEnabled,
        agent_visibility_updated_at: Date.now(),
        agent_visibility_updated_by: adminEmail || "admin",
      });
      setVisibility(next);
      cacheVisibility(collectionName, next);
      return next;
    } finally {
      setSaving(false);
    }
  };

  return (
    <AgentVisibilityContext.Provider value={{ ...visibility, loading, saving, updateVisibility }}>
      {children}
    </AgentVisibilityContext.Provider>
  );
}

export function AgentVisibilityProvider({ children }) {
  const sessionUser = useSelector(selectLogInUsername);
  const collectionName = sessionUser?.split("@")[0] || "";

  return (
    <AgentVisibilityStateProvider key={collectionName} collectionName={collectionName}>
      {children}
    </AgentVisibilityStateProvider>
  );
}

export function useAgentVisibility() {
  return useContext(AgentVisibilityContext);
}
